import { fetchFromRTM } from './index';
import * as constants from './constants';

class SearchResolver {
  constructor(connection) {
    this.connection = connection;
  }

  /**
   * Searches the entire course for a given text query. Returns a flat list
   * of results with per-chapter occurrence counts.
   *
   * @param {Object} payload
   * @param {string} payload.accountId
   * @param {string} payload.rootActivityId - Course-level activityId
   * @param {string} payload.searchText
   * @param {number} [payload.limit]
   * @param {number} [payload.offset]
   * @returns {Promise.<{searchText: string, totalCount: number, results: Array}>}
   */
  search = async ({ accountId, rootActivityId, searchText, limit, offset }) => {
    const message = {
      type: constants.RTM_AUTHOR_SEARCH,
      accountId,
      rootActivityId,
      searchText,
      limit,
      offset,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_SEARCH_OK,
      constants.RTM_AUTHOR_SEARCH_ERROR
    );

    const data = (response && response.data) || {};
    return {
      searchText: data.searchText != null ? data.searchText : searchText,
      totalCount: data.totalCount || 0,
      results: Array.isArray(data.results) ? data.results : [],
    };
  };

  /**
   * Returns lightweight suggestions for a partial search query.
   *
   * @param {Object} payload
   * @param {string} payload.accountId
   * @param {string} payload.rootActivityId
   * @param {string} payload.searchText - Partial text typed so far
   * @param {number} [payload.limit=5]
   * @returns {Promise.<{searchText: string, suggestions: Array.<{text: string}>}>}
   */
  suggest = async ({ accountId, rootActivityId, searchText, limit }) => {
    const message = {
      type: constants.RTM_AUTHOR_SEARCH_SUGGEST,
      accountId,
      rootActivityId,
      searchText,
      limit,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_SEARCH_SUGGEST_OK,
      constants.RTM_AUTHOR_SEARCH_SUGGEST_ERROR
    );

    const data = (response && response.data) || {};
    return {
      searchText: data.searchText != null ? data.searchText : searchText,
      suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
    };
  };

  /**
   * BACW-554 — Asks the backend to start building a search index for an
   * existing/imported course. BE: IndexActivityMessageHandler.
   * The OK ack fires AFTER Mercury walks the entire courseware tree and
   * pushes end-of-title to CSG (see IndexingService.indexForWorkspace),
   * not when the job is "queued". CSG indexing continues asynchronously
   * after that ack — caller must poll getIndexStatus to track completion.
   *
   * @param {Object} payload
   * @param {string} payload.activityId - Course-level activityId (rootActivityId)
   * @returns {Promise.<Object>} BE acknowledgement (empty body)
   */
  triggerIndexing = async ({ activityId }) => {
    const message = {
      type: constants.RTM_PROJECT_ACTIVITY_INDEX,
      activityId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PROJECT_ACTIVITY_INDEX_OK,
      constants.RTM_PROJECT_ACTIVITY_INDEX_ERROR
    );

    return (response && response.data) || {};
  };

  /**
   * BACW-840 — Returns the current indexing status for a course.
   * BE: IndexActivityStatusMessageHandler → IndexingService.getIndexStatusForWorkspace
   *      → proxies CSG GET End-of-Title; returns IndexForWorkspaceStatusResponse.
   *
   * @param {Object} payload
   * @param {string} payload.activityId - Course-level activityId (rootActivityId)
   * @returns {Promise.<{status?: string, totalCountInCSG?: number, totalFailedInCSG?: number, totalCountReceived?: number}>}
   *   status one of: NONE | INPROGRESS | INDEX_IN_PROGRESS | COMPLETED | DONE | ERROR
   *   (DONE confirmed by Mercury team 2026-05-19 — CSG returns this as the
   *    terminal "ingestion complete" value, alongside totalCountInCSG +
   *    totalCountReceived. Local Mercury with csgConfig.isEnabled=false
   *    returns an empty {data:{}} — treat as no-op.)
   *   isIndexStatusActive() in SearchPanel uppercases before comparing, so
   *   any casing returned by CSG is normalized.
   */
  getIndexStatus = async ({ activityId }) => {
    const message = {
      type: constants.RTM_PROJECT_ACTIVITY_INDEX_STATUS,
      activityId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PROJECT_ACTIVITY_INDEX_STATUS_OK,
      constants.RTM_PROJECT_ACTIVITY_INDEX_STATUS_ERROR
    );

    return (response && response.data) || {};
  };
}

export default SearchResolver;
